<script setup lang="ts">
import { ref } from 'vue'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Loader2, ArrowDownToLine } from 'lucide-vue-next'
import { API, HOST } from '@/utils/http'
import type { CombinedCustomer, ExternalAccount } from '@/types/bridge'

const props = defineProps<{
  customer: CombinedCustomer
  externalAccount: ExternalAccount
}>()

const emit = defineEmits<{
  (e: 'funded'): void
}>()

const open = ref(false)
const amount = ref('')
const submitting = ref(false)
const error = ref<string | null>(null)

const reset = () => {
  amount.value = ''
  error.value = null
}

const submit = async () => {
  error.value = null

  const parsed = parseFloat(amount.value)
  if (!amount.value || isNaN(parsed) || parsed <= 0) {
    error.value = 'Enter a valid amount'
    return
  }

  submitting.value = true
  try {
    await API({
      method: 'POST',
      url: `${HOST}/payments/bridge/transfers`,
      data: {
        sender_customer_id: props.customer.id,
        receiver_customer_id: props.customer.id,
        amount: String(parsed),
        source_payment_rail: 'ach_push',
        destination_payment_rail: 'base',
        external_account_id: props.externalAccount.id,
      }
    })

    emit('funded')
    open.value = false
    reset()
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to initiate funding'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger as-child>
      <Button variant="outline" size="sm" class="h-7 text-xs" @click.stop>
        <ArrowDownToLine class="h-3 w-3 mr-1" />
        Fund
      </Button>
    </DialogTrigger>
    <DialogContent class="sm:max-w-[400px]">
      <DialogHeader>
        <DialogTitle>Fund Account</DialogTitle>
        <DialogDescription>
          Debit <span class="font-medium">{{ externalAccount.bank_name }}</span> and deposit USDB into
          <span class="font-medium">{{ customer.name }}</span>'s wallet on Base.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4 py-2">
        <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {{ error }}
        </div>

        <div>
          <Label class="mb-2 block">Amount (USD)</Label>
          <Input
            v-model="amount"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="5.00"
            :disabled="submitting"
          />
        </div>
      </div>

      <div class="flex justify-end gap-3">
        <Button variant="outline" @click="open = false" :disabled="submitting">Cancel</Button>
        <Button @click="submit" :disabled="submitting || !amount">
          <Loader2 v-if="submitting" class="mr-2 h-4 w-4 animate-spin" />
          Initiate Funding
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
